import {
  RevenueLedgerVersionSchema,
  type RevenueLedgerCompareRow,
  type RevenueLedgerVersion,
} from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLedgerCompare } from '../../../hooks/useRevenueLedger.js';
import { REVENUE_VERSION_OPTIONS, versionLabelKey } from '../../../lib/revenue-ledger-view.js';
import { PendingMonthsNote } from './PendingMonthsNote.js';

/** @graph-connects none */
function otherVersion(version: RevenueLedgerVersion): RevenueLedgerVersion {
  return version === 'v0' ? 'v0.1' : 'v0';
}

/** @graph-connects none */
function yen(value: number): string {
  return value.toLocaleString('ja-JP');
}

/** @graph-connects none */
const TH =
  'border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]';
/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] px-3 py-1.5 align-top';

/**
 * 値の代わりに出す札（「対応なし」＝片方の版に行が無い、「データなし」＝行はあるが期間に値が無い）。
 * @graph-connects none
 */
function Tag({ kind }: { kind: 'noMapping' | 'noData' }) {
  const { t } = useTranslation();
  return (
    <span className="inline-block rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]">
      {kind === 'noMapping' ? t('revenue.compare.noMapping') : t('revenue.compare.noData')}
    </span>
  );
}

/** @graph-connects none */
function SideCell({ label, value }: { label: string | null; value: number | null }) {
  if (label === null) {
    return (
      <td className={TD}>
        <Tag kind="noMapping" />
      </td>
    );
  }
  return (
    <td className={TD}>
      <div className="text-xs text-[var(--color-text-secondary)]">{label}</div>
      <div className="text-right tabular-nums text-[var(--color-text-primary)]">
        {value === null ? <Tag kind="noData" /> : yen(value)}
      </div>
    </td>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計の版の比較タブ。2つの版と月の範囲を選び、対応表の行ごとに両版の値・差額・注記を並べ、片方にしかない行は「対応なし」、期間に値が無い側は 0 ではなく「データなし」を明示する。当月の値をまだ出さない月は月次タブと同じ注記を出す。
 * @graph-connects useLedgerCompare [calls] 版の比較の取得
 * @graph-connects PendingMonthsNote [calls] 当月の値をまだ出さない月の注記
 */
export function RevenueCompareView({
  monthOptions,
  defaultMonth,
}: {
  monthOptions: readonly string[];
  defaultMonth: string;
}) {
  const { t } = useTranslation();
  const [leftVersion, setLeftVersion] = useState<RevenueLedgerVersion>('v0');
  const [fromMonth, setFromMonth] = useState(defaultMonth);
  const [toMonth, setToMonth] = useState(defaultMonth);
  const rightVersion = otherVersion(leftVersion);
  const validRange = fromMonth <= toMonth;
  const query = useLedgerCompare({ leftVersion, rightVersion, fromMonth, toMonth }, validRange);
  const versionOptions = REVENUE_VERSION_OPTIONS.map((o) => ({
    value: o.version,
    label: t(o.labelKey),
  }));
  const monthSelectOptions = monthOptions.map((m) => ({ value: m, label: m }));

  return (
    <div className="mt-6 space-y-4">
      <Card className="flex flex-wrap items-end gap-4 p-4">
        <Select
          size="sm"
          label={t('revenue.compare.leftVersion')}
          options={versionOptions}
          value={leftVersion}
          onChange={(e) => {
            setLeftVersion(RevenueLedgerVersionSchema.parse(e.target.value));
          }}
        />
        <Select
          size="sm"
          label={t('revenue.compare.rightVersion')}
          options={versionOptions}
          value={rightVersion}
          onChange={(e) => {
            setLeftVersion(otherVersion(RevenueLedgerVersionSchema.parse(e.target.value)));
          }}
        />
        <Select
          size="sm"
          label={t('revenue.compare.fromMonth')}
          options={monthSelectOptions}
          value={fromMonth}
          onChange={(e) => {
            setFromMonth(e.target.value);
          }}
        />
        <Select
          size="sm"
          label={t('revenue.compare.toMonth')}
          options={monthSelectOptions}
          value={toMonth}
          onChange={(e) => {
            setToMonth(e.target.value);
          }}
        />
      </Card>
      <p className="text-xs text-[var(--color-text-secondary)]">{t('revenue.compare.rangeNote')}</p>

      {!validRange ? (
        <Card className="p-6">
          <p role="alert" className="text-sm text-[var(--color-text-secondary)]">
            {t('revenue.ledger.invalidRange')}
          </p>
        </Card>
      ) : query.isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <Card className="p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('revenue.compare.fetchError')}
          </p>
        </Card>
      ) : (
        <>
          <PendingMonthsNote months={query.data?.pendingMonths ?? []} />
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[var(--color-bg-base)]">
                  <th className={TH}>{t('revenue.compare.rowNo')}</th>
                  <th className={TH}>{t(versionLabelKey(leftVersion))}</th>
                  <th className={TH}>{t(versionLabelKey(rightVersion))}</th>
                  <th className={`${TH} text-right`}>{t('revenue.compare.diff')}</th>
                  <th className={TH}>{t('revenue.compare.note')}</th>
                </tr>
              </thead>
              <tbody>
                {(query.data?.rows ?? []).map((row: RevenueLedgerCompareRow) => (
                  <tr key={row.rowNo} className="hover:bg-[var(--color-bg-base)]">
                    <td className={`${TD} text-[var(--color-text-muted)]`}>{row.rowNo}</td>
                    <SideCell label={row.leftLabel} value={row.leftValue} />
                    <SideCell label={row.rightLabel} value={row.rightValue} />
                    <td className={`${TD} text-right font-semibold tabular-nums`}>
                      {row.leftLabel === null || row.rightLabel === null ? (
                        <Tag kind="noMapping" />
                      ) : row.diff === null ? (
                        <Tag kind="noData" />
                      ) : (
                        yen(row.diff)
                      )}
                    </td>
                    <td className={`${TD} text-xs text-[var(--color-text-secondary)]`}>
                      {row.note}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
